import { useSignal } from "@preact/signals";
import type { Channel } from "~/web/src/channel";

// The explorer's credentials ask (spec: web-workspace-spec "Protocol", Ask).
// Submitting stores the pair on the project through update_project; the
// workspace sees the pending ask for this domain and answers the explorer
// itself — the secret never becomes a chat line. Skip is an ordinary chat
// reply the explorer reads as an instruction.

const SKIP_TEXT = "Continue without signing in — use the public pages only.";

export function CredentialsCard({
  channel,
  domain,
  note,
}: {
  channel: Channel;
  domain: string;
  note: string;
}) {
  const stored = channel.project.value?.credentials[domain]?.username;
  const username = useSignal(stored ?? "");
  const password = useSignal("");
  const busy = useSignal(false);

  async function submit(e: Event) {
    e.preventDefault();
    if (busy.value) return;
    busy.value = true;
    await channel.send({
      kind: "update_project",
      credentials: { [domain]: { username: username.value, password: password.value } },
    });
    busy.value = false;
  }

  // ph-no-capture: session replay masks the whole card (web/src/analytics.ts).
  return (
    <form class="cred ph-no-capture" onSubmit={(e) => void submit(e)}>
      <h3>This site asks for a sign-in</h3>
      <p>
        The pages behind <span class="mono">{domain}</span> need an account. Enter credentials and
        we sign in for you, then keep exploring. Use a demo or test account if you have one.
      </p>
      {note && <p class="note">{note}</p>}
      <div class="grid">
        <div class="field">
          <input
            type="text"
            placeholder="Email or username"
            aria-label="Email or username"
            value={username.value}
            onInput={(e) => {
              username.value = e.currentTarget.value;
            }}
            required
          />
        </div>
        <div class="field">
          <input
            type="password"
            placeholder="Password"
            aria-label="Password"
            value={password.value}
            onInput={(e) => {
              password.value = e.currentTarget.value;
            }}
            required
          />
        </div>
      </div>
      <div class="row">
        <button class="btn" type="submit" disabled={busy.value}>
          Sign in and continue
        </button>
        <button
          class="btn ghost"
          type="button"
          onClick={() => void channel.send({ kind: "chat", text: SKIP_TEXT })}
        >
          Skip, use public pages only
        </button>
        <span class="fine">
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="4" y="10" width="16" height="11" rx="2" />
            <path d="M8 10V7a4 4 0 018 0v3" />
          </svg>
          Kept for this project only
        </span>
      </div>
    </form>
  );
}
